import { createAccountId, createFirmId, createLawyerId } from '@domain/value-objects';
import type { Unbrand } from '@shared/value-objects/brand';
import {
  ResponseMappingError,
  optVO,
  reqRecord,
  reqVO,
} from '@shared/value-objects/parse-helpers';
import {
  createEmail,
  createISODate,
  createPhone,
  createPostalCode,
  createUrl,
} from '@shared/value-objects';
import { parseFirmBulkSalesDecision } from '@domain/bulk-sales/parse';
import type { Firm, RepresentativeLawyerEmbed } from './model';

/// API の生レスポンス (`unknown`) を検証して branded な `Firm` にする門番 (parse, don't validate)。
/// API クライアントは `unknown` を返すだけで検証しない。ここが唯一の検証点。
/// 失敗方針 (#471): 必須 id・present かつ不正な任意フィールドは `ResponseMappingError` を throw
/// (store の catch が UI エラーに変換)。任意の値なしは null。`raw` 値はログに載せない。
/// 詳細は GitHub Discussion #475 (フロント FSD: API 境界の VO 適用方針)
/// https://github.com/AX-UNLEARN/LawyerPartnerCRM/discussions/475 を参照。
/// 代表弁護士は 0..N の `representative_lawyer_ids` / `representative_lawyers` (join table 由来)
/// のみを受ける。 旧単数列 (representative_lawyer_id / representative_lawyer) の pre-0..N
/// rolling 変換は P1.5 の二重持ち解消で撤去した (backend は常に複数形を返す)。

function parseRepresentativeLawyer(
  raw: unknown,
  field: string,
): RepresentativeLawyerEmbed {
  const o = reqRecord(raw, field);
  return {
    ...(o as Unbrand<RepresentativeLawyerEmbed>),
    id: reqVO(o.id, createLawyerId, `${field}.id`),
  };
}

/// 代表弁護士 (0..N) の id 配列。 配列でなければ契約破れとして throw し、
/// 各要素を branded LawyerId に検証する。 0 名 = 空配列は正常。
function parseRepresentativeLawyerIds(dto: Unbrand<Firm>): Firm['representative_lawyer_ids'] {
  const raw = dto.representative_lawyer_ids;
  if (!Array.isArray(raw)) {
    throw new ResponseMappingError('firm.representative_lawyer_ids', 'must be an array');
  }
  return raw.map((v, i) => reqVO(v, createLawyerId, `firm.representative_lawyer_ids[${i}]`));
}

function parseRepresentativeLawyers(dto: Unbrand<Firm>): Firm['representative_lawyers'] {
  const raw = dto.representative_lawyers;
  if (raw == null) return raw as null | undefined;
  if (!Array.isArray(raw)) {
    throw new ResponseMappingError('firm.representative_lawyers', 'must be an array');
  }
  return raw.map((v, i) => parseRepresentativeLawyer(v, `firm.representative_lawyers[${i}]`));
}

export function parseFirm(raw: unknown): Firm {
  reqRecord(raw, 'firm');
  // 非 VO フィールドの型付き spread のための trust 境界 (構造は reqRecord で担保済み)。
  const dto = raw as Unbrand<Firm>;
  return {
    ...dto,
    id: reqVO(dto.id, createFirmId, 'firm.id'),
    // phone_note は VO を被せない自由テキスト。値検証はしないが、欠落/undefined は
    // `string | null` 契約に合わせて null へ正規化する (`...dto` 素通しだと undefined が漏れる)。
    phone_note: dto.phone_note ?? null,
    county: dto.county ?? null,
    ward: dto.ward ?? null,
    established_date: optVO(dto.established_date, createISODate, 'firm.established_date'),
    representative_lawyer_ids: parseRepresentativeLawyerIds(dto),
    postal_code: optVO(dto.postal_code, createPostalCode, 'firm.postal_code'),
    phone: optVO(dto.phone, createPhone, 'firm.phone'),
    email: optVO(dto.email, createEmail, 'firm.email'),
    fax: optVO(dto.fax, createPhone, 'firm.fax'),
    website_url: optVO(dto.website_url, createUrl, 'firm.website_url'),
    assignee_account_id: optVO(dto.assignee_account_id, createAccountId, 'firm.assignee_account_id'),
    representative_lawyers: parseRepresentativeLawyers(dto),
    bulk_sales_decision: parseFirmBulkSalesDecision(
      dto.bulk_sales_decision,
      'firm.bulk_sales_decision',
    ),
  };
}
